import { getDetailedContactView } from 'API/linkedin-funnel';
import { updateContactIsActive } from 'API/linkedin-funnel-search';
import { FunnelContact } from 'features/co-pilot/co-pilot.types';
import { useUserData } from 'hooks/use-user-data';
import _ from 'lodash';
import { useState } from 'react';
import useErrorStore from 'stores/error-store';
import { Assessment } from 'types/analysis-result.types';
import { ProfileValues } from 'types/profile.types';
import { isUncertainSex } from './campaign-building-panel/campaign-pipeline/contact-sex-utils';
import { CompanyAbout, ContactAbout, ContactDetails } from './campaign-builder.types';
import useContactDrawerStore from 'stores/contact-drawer-store';

const useContactDrawer = (refreshContactList?: () => void, funnelContacts?: FunnelContact[]) => {
  useUserData();
  const handleOpenErrorDialog = useErrorStore((state) => state.handleOpenErrorDialog);
  const setActiveDrawerContact = useContactDrawerStore((state) => state.setActiveDrawerContact);

  const [contactDrawer, setContactDrawer] = useState<{
    open: boolean;
    loading: boolean;
    contactIndex: number | null;
    aiJudgement: string | undefined;
    aiJudgementExplanation: string | undefined;
    assessment: Assessment | undefined;
    percentiles: ProfileValues | undefined;
    isActive: boolean | undefined;
    contactDetails: ContactDetails | null;
    aboutContact: ContactAbout | null;
    aboutCompany: CompanyAbout | null;
    linkedInURL: string | undefined;
    profileId: string | undefined;
    funnelId: string | undefined;
    toggleDisabled: boolean;
  }>({
    open: false,
    loading: false,
    contactIndex: null,
    aiJudgement: undefined,
    aiJudgementExplanation: undefined,
    assessment: undefined,
    percentiles: undefined,
    isActive: undefined,
    contactDetails: null,
    aboutContact: null,
    aboutCompany: null,
    linkedInURL: undefined,
    profileId: undefined,
    funnelId: undefined,
    toggleDisabled: false,
  });

  const handleOpenContactDrawer = (linkedInURL: string | undefined, profileId: string | undefined, campaignId: string | undefined) => {
    return async () => {
      const res = await getDetailedContactView(linkedInURL, profileId, campaignId);

      if (!res.error) {
        const index = funnelContacts?.findIndex((c) => c.linkedInURL === linkedInURL) ?? null;
        const contact = funnelContacts?.find((c) => c.linkedInURL === linkedInURL);

        setActiveDrawerContact(linkedInURL);

        setContactDrawer((prev) => ({
          ...prev,
          open: true,
          contactIndex: index,
          aiJudgement: res.aiJudgement,
          aiJudgementExplanation: res.aiJudgementExplanation,
          assessment: res.assessment,
          percentiles: res.percentiles,
          isActive: res.contactDetails.isActive,
          contactDetails: res.contactDetails,
          aboutContact: res.aboutContact,
          aboutCompany: res.aboutCompany,
          linkedInURL,
          profileId,
          funnelId: campaignId,
          toggleDisabled: contact?.isDeleted || contact?.isBlacklisted || contact?.isSuspended || isUncertainSex(contact?.sex) || false,
        }));
        refreshContactList?.();
      } else {
        handleOpenErrorDialog(res.error, '/linkedinAccounts/getDetailedContactView', 'get');
      }
    };
  };

  const handleGetNextContact = async () => {
    const { linkedInURL, funnelId, contactIndex } = contactDrawer;
    if (_.isNil(linkedInURL) || _.isNil(funnelContacts) || _.isNil(funnelId)) return;

    const lastContactIndex = contactIndex ?? 0;

    const currentContactIndex = funnelContacts.findIndex((c) => c.linkedInURL === linkedInURL);
    const nextContactIndex =
      currentContactIndex === funnelContacts.length - 1 ? 0 : currentContactIndex === -1 ? lastContactIndex : currentContactIndex + 1;
    const nextContact = funnelContacts[nextContactIndex];
    const nextContactToggleDisabled =
      nextContact?.isDeleted ||
      nextContact?.isBlacklisted ||
      nextContact?.isSuspended ||
      isUncertainSex(nextContact?.sex) ||
      false;

    setContactDrawer((prev) => ({ ...prev, open: true, contactIndex: nextContactIndex }));

    if (!_.isNil(nextContact)) {
      const res = await getDetailedContactView(nextContact.linkedInURL, nextContact.profileIds?.linkedin, funnelId);

      if (!res.error) {
        setActiveDrawerContact(nextContact.linkedInURL);
        setContactDrawer((prev) => ({
          ...prev,
          open: true,
          aiJudgement: res.aiJudgement,
          aiJudgementExplanation: res.aiJudgementExplanation,
          assessment: res.assessment,
          percentiles: res.percentiles,
          contactDetails: res.contactDetails,
          aboutContact: res.aboutContact,
          aboutCompany: res.aboutCompany,
          contactIndex: nextContactIndex,
          linkedInURL: nextContact.linkedInURL,
          profileId: res.profileId,
          isActive: res.contactDetails.isActive,
          toggleDisabled: nextContactToggleDisabled,
        }));
        refreshContactList?.();
      } else {
        handleOpenErrorDialog(res.error, '/linkedinAccounts/getDetailedContactView', 'get');
      }
    }
  };

  const handleGetPrevContact = async () => {
    const { linkedInURL, funnelId, contactIndex } = contactDrawer;
    if (_.isNil(linkedInURL) || _.isNil(funnelContacts) || _.isNil(funnelId)) return;

    const lastContactIndex = contactIndex ?? 0;

    const currentContactIndex = funnelContacts.findIndex((c) => c.linkedInURL === linkedInURL);
    const prevContactIndex =
      currentContactIndex === 0 ? funnelContacts.length - 1 : currentContactIndex === -1 ? lastContactIndex : currentContactIndex - 1;
    const prevContact = funnelContacts[prevContactIndex];
    const prevContactToggleDisabled =
      prevContact?.isDeleted ||
      prevContact?.isBlacklisted ||
      prevContact?.isSuspended ||
      isUncertainSex(prevContact?.sex) ||
      false;

    setContactDrawer((prev) => ({ ...prev, open: true, contactIndex: prevContactIndex }));

    if (!_.isNil(prevContact)) {
      const res = await getDetailedContactView(prevContact.linkedInURL, prevContact.profileIds?.linkedin, funnelId);

      if (!res.error) {
        setActiveDrawerContact(prevContact.linkedInURL);
        setContactDrawer((prev) => ({
          ...prev,
          open: true,
          aiJudgement: res.aiJudgement,
          aiJudgementExplanation: res.aiJudgementExplanation,
          assessment: res.assessment,
          percentiles: res.percentiles,
          contactDetails: res.contactDetails,
          aboutContact: res.aboutContact,
          aboutCompany: res.aboutCompany,
          contactIndex: prevContactIndex,
          linkedInURL: prevContact.linkedInURL,
          profileId: res.profileId,
          isActive: res.contactDetails.isActive,
          toggleDisabled: prevContactToggleDisabled,
        }));
        refreshContactList?.();
      } else {
        handleOpenErrorDialog(res.error, '/linkedinAccounts/getDetailedContactView', 'get');
      }
    }
  };

  const handleCloseContactDrawer = () => {
    setActiveDrawerContact(undefined);
    setContactDrawer({
      open: false,
      loading: false,
      contactIndex: null,
      aiJudgement: undefined,
      aiJudgementExplanation: undefined,
      assessment: undefined,
      percentiles: undefined,
      isActive: undefined,
      contactDetails: null,
      aboutContact: null,
      aboutCompany: null,
      linkedInURL: undefined,
      profileId: undefined,
      funnelId: undefined,
      toggleDisabled: false,
    });
  };

  const handleUpdateDrawerContactIsActive = (newIsActive: boolean) => {
    return async () => {
      const { linkedInURL, funnelId } = contactDrawer;

      if (_.isNil(linkedInURL) || _.isNil(funnelId)) return;

      const res = await updateContactIsActive(funnelId, [linkedInURL], newIsActive);

      if (!res.error) {
        setContactDrawer((prev) => ({ ...prev, isActive: newIsActive }));
        refreshContactList?.();
      } else {
        handleOpenErrorDialog(res?.error, '/campaigns/:id/contacts', 'put');
      }
    };
  };

  return {
    contactDrawer,
    handleOpenContactDrawer,
    handleGetNextContact,
    handleGetPrevContact,
    handleCloseContactDrawer,
    handleUpdateDrawerContactIsActive,
  };
};

export default useContactDrawer;
